import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavBar from '../NavBar';
import { supabase } from '../supabaseClient';
import { auth } from '../firebaseConfig';
import { TableIcon, UserGroupIcon, CogIcon, PlusIcon } from '@heroicons/react/solid';
import Modal from 'react-modal';
import Notifications from '../components/Notifications';

function OrganizationDisplay() {
  const { orgID } = useParams(); // this is orgs.orgID (bigint)
  const navigate = useNavigate();
  const notificationRef = useRef(null);

  const orgColors = ['#0d2f81', '#0079bf', '#519839', '#61bd4f', '#d29034', '#f2d600', '#b04632'];

  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('projects');
  const [createProjectModalIsOpen, setCreateProjectModalIsOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');

  const [showDesktopMessage, setShowDesktopMessage] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Track role selection per-member instead of one shared selectedRole for all cards
  const [selectedRoles, setSelectedRoles] = useState({}); // { [orgMembers.id]: "Member" | "Editor" }

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      maxWidth: '400px',
      padding: '2rem',
      backgroundColor: '#37474f',
      color: '#f7fafc',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      borderRadius: '8px',
      border: '1px solid #1a202c',
      outline: '1px solid #000000',
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
  };

  const orgIdAsNumberOrString = orgID; // Supabase can accept string "123" for bigint equality

  const fetchData = async () => {
    if (!orgIdAsNumberOrString) return;

    try {
      setLoading(true);

      // ✅ orgs primary key is orgID (NOT id)
      const { data: orgData, error: orgError } = await supabase
        .from('orgs')
        .select('*')
        .eq('orgID', orgIdAsNumberOrString)
        .maybeSingle();

      if (orgError) throw orgError;
      setOrg(orgData);

      const { data: orgMembersData, error: orgMembersError } = await supabase
        .from('orgMembers')
        .select('*')
        .eq('orgID', orgIdAsNumberOrString);

      if (orgMembersError) throw orgMembersError;
      setOrgMembers(orgMembersData || []);

      // Your schema shows projects columns: projectID, projectName, projectDesc, orgID
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('orgID', orgIdAsNumberOrString);

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => setUser(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orgID]);

  useEffect(() => {
    const handleResize = () => setShowDesktopMessage(window.innerWidth < 650);
    setShowDesktopMessage(window.innerWidth < 650);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch current user's role in this org
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (!user || !orgIdAsNumberOrString) return;

        const { data, error } = await supabase
          .from('orgMembers')
          .select('memberRole')
          .eq('orgID', orgIdAsNumberOrString)
          .eq('memberID', user.uid)
          .maybeSingle();

        if (error) throw error;
        setUserRole(data?.memberRole || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [user, orgID]);

  // ✅ Fix role select handling per member
  const handleRoleChange = (memberRowId, role) => {
    setSelectedRoles((prev) => ({ ...prev, [memberRowId]: role }));
  };

  const handleUpdateRole = async (memberRowId) => {
    try {
      const newRole = selectedRoles[memberRowId];
      if (!newRole) return;

      // ✅ orgMembers primary key is id (bigint). Use it to update.
      const { error } = await supabase
        .from('orgMembers')
        .update({ memberRole: newRole })
        .eq('id', memberRowId);

      if (error) throw error;

      await fetchData();
      notificationRef.current?.showAlertMessage('Member role updated!', true);
    } catch (error) {
      console.error('Error updating user role:', error);
      notificationRef.current?.showAlertMessage('Failed to update role.', false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const generateNewApiKey = async () => {
    try {
      if (!orgIdAsNumberOrString) return;

      const newApiKey = Array.from({ length: 4 }, () => Math.random().toString(36).substring(2, 8)).join('-');

      // ✅ update by orgID, not id
      const { error } = await supabase.from('orgs').update({ api_key: newApiKey }).eq('orgID', orgIdAsNumberOrString);

      if (error) throw error;

      notificationRef.current?.showAlertMessage('New API Key Generated!', true);
      await fetchData();
    } catch (error) {
      console.error('Error generating new API key:', error);
      notificationRef.current?.showAlertMessage('Failed to generate API key.', false);
    }
  };

  const copyApiKey = async (apiKey) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      notificationRef.current?.showAlertMessage('Copied API Key To Clipboard.', true);
    } catch (error) {
      console.error('Failed to copy API key:', error);
      notificationRef.current?.showAlertMessage('Failed to copy API key.', false);
    }
  };

  const generateNewJoinCode = async () => {
    try {
      if (!orgIdAsNumberOrString) return;
      const joinCode = Math.random().toString(36).substring(2, 8);

      // ✅ update by orgID, not id
      const { error } = await supabase.from('orgs').update({ joinCode }).eq('orgID', orgIdAsNumberOrString);

      if (error) throw error;

      notificationRef.current?.showAlertMessage('New Join Code Generated!', true);
      await fetchData();
    } catch (error) {
      console.error('Error generating new join code:', error);
      notificationRef.current?.showAlertMessage('Failed to generate join code.', false);
    }
  };

  const copyJoinCode = async (joinCode) => {
    try {
      await navigator.clipboard.writeText(joinCode);
      notificationRef.current?.showAlertMessage('Copied Join Code To Clipboard!', true);
    } catch (error) {
      console.error('Failed to copy Join Code:', error);
      notificationRef.current?.showAlertMessage('Failed to copy join code.', false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      if (!orgIdAsNumberOrString) return;

      // ✅ schema uses projectName / projectDesc, NOT name/description (based on your screenshot)
      const { error } = await supabase.from('projects').insert([
        {
          projectName: projectName.trim(),
          projectDesc: projectDescription.trim(),
          orgID: orgIdAsNumberOrString,
        },
      ]);

      if (error) throw error;

      setCreateProjectModalIsOpen(false);
      setProjectName('');
      setProjectDescription('');
      await fetchData();
      notificationRef.current?.showAlertMessage('Project created successfully!', true);
    } catch (error) {
      console.error('Error creating new project:', error);
      notificationRef.current?.showAlertMessage('Failed to create project.', false);
    }
  };

  // Realtime subscriptions (fixed channels to use orgID column name)
  useEffect(() => {
    if (!orgIdAsNumberOrString) return;

    const projectSubscription = supabase
      .channel(`public:projects:orgID=eq.${orgIdAsNumberOrString}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, () => fetchData())
      .subscribe();

    const orgMembersSubscription = supabase
      .channel(`public:orgMembers:orgID=eq.${orgIdAsNumberOrString}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orgMembers' }, () => fetchData())
      .subscribe();

    // ✅ orgs uses orgID
    const orgSubscription = supabase
      .channel(`public:orgs:orgID=eq.${orgIdAsNumberOrString}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orgs' }, () => fetchData())
      .subscribe();

    return () => {
      supabase.removeChannel(projectSubscription);
      supabase.removeChannel(orgMembersSubscription);
      supabase.removeChannel(orgSubscription);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgID]);

  if (!user) {
    return (
      <div className="dashboard bg-dark-main h-full min-h-screen">
        <NavBar />
        <div className="mx-auto p-4 mt-12 container">
          <p className="text-white-default mb-12">Please log in to view this page.</p>
        </div>
      </div>
    );
  }

  if (showDesktopMessage) {
    return (
      <div className="dashboard bg-dark-main h-screen">
        <NavBar />
        <div className="mx-auto p-4 mt-12 container">
          <h1 className="text-white-default text-xl">
            This page is not available on mobile devices. Please use a desktop or laptop to view this page.
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard bg-dark-main h-full min-h-screen transition ease-in-out duration-300">
      <NavBar />

      <div className="mx-auto p-4 mt-12 container md:w-full lg:w-3/4 xl:w-2/3 sm:w-full">
        <div className="flex items-center mb-4">
          <h1 className="text-2xl font-bold text-white-default">{org ? org.orgName : 'Organization'}</h1>
          {org && (
            <div
              title="This determines whether people outside the organisation can view your projects."
              className="ml-2 select-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium text-gray border border-card-purple"
            >
              {org.isPrivate ? 'Private' : 'Public'}
            </div>
          )}
        </div>

        <hr />

        <div className="mb-8">
          <ul className="flex bg-dark-lightMain p-4 text-white-default justify-between w-full md:w-1/2 lg:w-1/3 rounded-md select-none space-x-4">
            <li
              className={`cursor-pointer inline-flex hover:text-dark-500 ${
                activeTab === 'projects' ? 'text-text-lightPurple font-bold underline select-none' : 'text-gray-500'
              } transition ease-in-out duration-100`}
              onClick={() => setActiveTab('projects')}
            >
              <TableIcon className="h-6 w-5 mr-2" /> Projects
            </li>

            <li
              className={`cursor-pointer inline-flex hover:text-dark-500 ${
                activeTab === 'members' ? 'text-text-lightPurple font-bold underline select-none' : 'text-gray-500'
              } transition ease-in-out duration-100`}
              onClick={() => setActiveTab('members')}
            >
              <UserGroupIcon className="h-6 w-5 mr-2" /> Members
            </li>

            <li
              className={`cursor-pointer inline-flex hover:text-dark-500 ${
                activeTab === 'settings' ? 'text-text-lightPurple font-bold underline select-none' : 'text-gray-500'
              } transition ease-in-out duration-100`}
              onClick={() => setActiveTab('settings')}
            >
              <CogIcon className="h-6 w-5 mr-2" /> Settings
            </li>
          </ul>
        </div>

        <div className="bg-card-default rounded-lg shadow-lg p-6">
          {activeTab === 'projects' && (
            <>
              <h2 className="text-xl font-semibold mb-4 text-white-default">Projects</h2>

              <div className="mt-6">
                {userRole === 'Owner' || userRole === 'Editor' ? (
                  <button
                    onClick={() => setCreateProjectModalIsOpen(true)}
                    className="inline-flex mb-5 bg-purple hover:bg-card-purple text-white-default py-2 px-4 rounded transition ease-in-out duration-100"
                  >
                    <PlusIcon className="h-6 w-5 mr-1" />
                    Create New Project
                  </button>
                ) : (
                  <p className="text-red mb-5">You are not authorized to create projects.</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                  <p className="text-white-default">Loading projects...</p>
                ) : (
                  projects.map((project) => (
                    <div
                      key={project.projectID}
                      className="bg-dark-300 h-32 p-4 rounded-md shadow-md border-t-4 cursor-pointer hover:scale-[101.5%] transition ease-in-out duration-200"
                      style={{ borderColor: '#7e5bef' }}
                    >
                      <h3 className="text-lg text-white-default font-semibold mb-2">
                        {project.projectName || '(Untitled project)'}
                      </h3>
                      <p className="text-sm text-dark-500 overflow-hidden overflow-ellipsis w-100">
                        {project.projectDesc || ''}
                      </p>
                    </div>
                  ))
                )}

                {!loading && projects.length === 0 && (
                  <div className="bg-gray-100 p-2 rounded-md">
                    <p className="text-white-default">No projects found for this organization.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'settings' && org && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-white-default">Organization Settings</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-dark-300 h-auto p-4 rounded-md shadow-md transition ease-in-out duration-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg text-white-default font-semibold mb-2">Organization Name</h3>
                    <p className="text-sm text-dark-500 overflow-hidden overflow-ellipsis w-100">{org.orgName}</p>
                  </div>
                </div>

                <div className="bg-dark-300 h-auto p-4 rounded-md shadow-md transition ease-in-out duration-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg text-white-default font-semibold mb-2">Organization Description</h3>
                    <p className="text-sm text-dark-500 overflow-hidden overflow-ellipsis w-100">{org.orgDesc}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-dark-300 h-auto p-4 rounded-md shadow-md transition ease-in-out duration-200 mt-4">
                  <h3 className="text-lg text-white-default font-semibold mb-2">API Key</h3>

                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      value={org.api_key || ''}
                      readOnly
                      className="text-sm text-dark-500 bg-dark-200 p-2 rounded w-full mr-2"
                    />
                    <button
                      onClick={() => copyApiKey(org.api_key)}
                      className="inline-flex bg-purple hover:bg-card-purple text-white-default py-2 px-5 rounded transition ease-in-out duration-100"
                    >
                      Copy
                    </button>
                  </div>

                  <button
                    onClick={generateNewApiKey}
                    className="inline-flex mt-3 bg-purple hover:bg-card-purple text-white-default py-2 px-5 rounded transition ease-in-out duration-100"
                  >
                    Generate New API Key
                  </button>
                </div>

                <div className="bg-dark-300 h-auto p-4 rounded-md shadow-md transition ease-in-out duration-200 mt-4">
                  <h3 className="text-lg text-white-default font-semibold mb-2">Join Code</h3>

                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      value={org.joinCode || ''}
                      readOnly
                      className="text-sm text-dark-500 bg-dark-200 p-2 rounded w-full mr-2"
                    />
                    <button
                      onClick={() => copyJoinCode(org.joinCode)}
                      className="inline-flex bg-purple hover:bg-card-purple text-white-default py-2 px-5 rounded transition ease-in-out duration-100"
                    >
                      Copy
                    </button>
                  </div>

                  <button
                    onClick={generateNewJoinCode}
                    className="inline-flex mt-3 bg-purple hover:bg-card-purple text-white-default py-2 px-5 rounded transition ease-in-out duration-100"
                  >
                    Generate New Join Code
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-white-default">Members</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:w-[70vw] xl:grid-cols-3 gap-4">
                {orgMembers.map((member) => (
                  <div
                    key={member.id}
                    className="bg-dark-300 h-auto p-4 rounded-md shadow-md transition ease-in-out duration-200 flex items-center"
                  >
                    <div className="flex-shrink-0 mr-4">
                      {member.profilePicture ? (
                        <img className="h-12 w-12 rounded-full" src={member.profilePicture} alt="Profile" />
                      ) : (
                        <div className="h-12 w-12 bg-gray-400 rounded-full" />
                      )}
                    </div>

                    <div className="flex-grow">
                      <div>
                        <h3 className="text-lg text-white-default font-semibold mb-2">{member.memberName}</h3>
                        <p className="text-sm text-dark-500 overflow-hidden overflow-ellipsis w-100">{member.memberRole}</p>
                      </div>

                      {member.memberID === user.uid ? (
                        <p className="text-red-500">You cannot edit your own role.</p>
                      ) : userRole === 'Owner' || userRole === 'Editor' ? (
                        member.memberRole === 'Owner' ? (
                          <p className="text-red-500">You cannot edit the Owner.</p>
                        ) : (
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-dark-500 mr-2 mb-3">Change Role:</label>
                            <div className="inline-flex">
                              <select
                                className="block cursor-pointer appearance-none border border-dark-300 bg-dark-100 text-dark-700 py-2 px-3 rounded-md mr-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                value={selectedRoles[member.id] || member.memberRole || 'Member'}
                                onChange={(e) => handleRoleChange(member.id, e.target.value)}
                              >
                                <option value="Member">Member</option>
                                <option value="Editor">Editor</option>
                              </select>

                              <button
                                onClick={() => handleUpdateRole(member.id)}
                                className="py-2 px-4 bg-purple hover:bg-card-purple text-white-default rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              >
                                Update
                              </button>
                            </div>
                          </div>
                        )
                      ) : (
                        <p className="text-red-500">You are not authorized to edit members.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      <Modal
        isOpen={createProjectModalIsOpen}
        onRequestClose={() => setCreateProjectModalIsOpen(false)}
        style={customStyles}
        ariaHideApp={false}
        contentLabel="Create Project Modal"
      >
        <h2 className="text-lg font-semibold mb-4 text-gray-800">Create Project</h2>

        <button
          onClick={() => setCreateProjectModalIsOpen(false)}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <form onSubmit={handleCreateProject}>
          <label htmlFor="projectName" className="block text-sm font-medium text-gray-700">
            Project Name:
          </label>
          <input
            type="text"
            id="projectName"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="block w-full outline outline-2 outline-purple p-3 mt-1 mb-5 bg-transparent rounded-md text-white-default focus:outline-text-lightPurple focus:border-none transition ease-in-out duration-300"
            placeholder="Enter project name"
            maxLength={50}
            required
          />

          <label htmlFor="projectDesc" className="block text-sm font-medium text-gray-700">
            Project Description:
          </label>
          <input
            type="text"
            id="projectDesc"
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
            className="block w-full outline outline-2 outline-purple p-3 mt-1 mb-5 bg-transparent rounded-md text-white-default focus:outline-text-lightPurple focus:border-none transition ease-in-out duration-300"
            placeholder="Enter project description"
            maxLength={50}
            required
          />

          <button
            type="submit"
            disabled={projectName.trim() === '' || projectDescription.trim() === ''}
            className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition ease-in-out duration-300"
          >
            Create Project
          </button>
        </form>
      </Modal>

      <Notifications ref={notificationRef} />
    </div>
  );
}

export default OrganizationDisplay;
